/**
 * [Gurumi YouTube]
 * 역할: yesmoney EditorYoutube 포팅
 * 작성/수정일: 2026-07-29
 */
import Youtube, { getEmbedUrlFromYoutubeUrl } from '@tiptap/extension-youtube';
import { mergeAttributes, ResizableNodeView } from '@tiptap/core';
import { clampEditorMediaSize, syncResizableNodeMaxSize } from '../utils.js';

const YOUTUBE_RESIZE = {
  enabled: true,
  directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
  minWidth: 240,
  minHeight: 135,
  alwaysPreserveAspectRatio: true,
};

function resolveAlignment(value) {
  return value === 'left' || value === 'right' || value === 'center' ? value : 'center';
}

function applyAlignmentToContainer(container, alignment) {
  container.style.display = 'flex';
  container.style.width = '100%';
  container.style.maxWidth = '100%';
  container.style.boxSizing = 'border-box';
  container.style.justifyContent =
    alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
  container.dataset.align = alignment;
}

function syncYoutubeAlignment(dom, node) {
  dom.setAttribute('data-align', resolveAlignment(node.attrs.alignment));
}

function syncYoutubeSize(dom, node) {
  const width = Number(node.attrs.width ?? 640);
  dom.style.aspectRatio = '16 / 9';
  dom.style.maxWidth = '100%';
  if (width > 0) {
    dom.style.width = `${width}px`;
    dom.style.height = 'auto';
  }
}

function buildEmbedSrc(node, options) {
  return (
    getEmbedUrlFromYoutubeUrl({
      url: String(node.attrs.src ?? ''),
      allowFullscreen: options.allowFullscreen,
      controls: options.controls,
      modestBranding: options.modestBranding,
      nocookie: options.nocookie,
      startAt: Number(node.attrs.start ?? 0),
    }) ?? String(node.attrs.src ?? '')
  );
}

function createYoutubeIframe(node, HTMLAttributes, options) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('allowfullscreen', 'true');
  iframe.setAttribute(
    'allow',
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
  );
  const merged = mergeAttributes(options.HTMLAttributes ?? {}, HTMLAttributes);
  Object.entries(merged).forEach(([key, value]) => {
    if (value == null || key === 'src') return;
    iframe.setAttribute(key, String(value));
  });
  const src = buildEmbedSrc(node, options);
  if (src) iframe.setAttribute('src', src);
  return iframe;
}

export const EditorYoutube = Youtube.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      alignment: {
        default: 'center',
        rendered: false,
        parseHTML: (el) => {
          const container = el.closest('[data-youtube-video]');
          const explicit = container?.getAttribute('data-align');
          if (explicit === 'left' || explicit === 'right' || explicit === 'center') return explicit;
          return 'center';
        },
      },
    };
  },

  renderHTML(props) {
    // [YouTube 삽입] leaf 노드에는 content hole(0) 금지 — parent iframe 스펙 유지
    // 작성/수정일: 2026-07-29
    const base = this.parent?.(props);
    if (!Array.isArray(base)) {
      return ['div', { 'data-youtube-video': '', class: 'editor-youtube' }];
    }
    const [tag, attrs, ...children] = base;
    const alignment = resolveAlignment(props.node.attrs.alignment);
    const width = Number(props.node.attrs.width ?? 640);
    return [
      tag,
      {
        ...(typeof attrs === 'object' && attrs !== null ? attrs : {}),
        'data-align': alignment,
        class: 'editor-youtube',
        style: `width:${width}px;max-width:100%;aspect-ratio:16/9;`,
      },
      ...children,
    ];
  },

  addNodeView() {
    if (typeof document === 'undefined' || !YOUTUBE_RESIZE.enabled) return null;
    const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } = YOUTUBE_RESIZE;

    return ({ node, getPos, HTMLAttributes, editor, extension }) => {
      const el = document.createElement('div');
      el.setAttribute('data-youtube-video', '');
      el.classList.add('editor-youtube');
      el.style.position = 'relative';
      el.style.overflow = 'hidden';
      el.style.marginBlock = '1.5rem';
      el.style.borderRadius = 'var(--g5-radius-lg)';
      el.style.background = '#000';
      el.style.cursor = 'grab';
      syncYoutubeAlignment(el, node);
      syncYoutubeSize(el, node);
      el.appendChild(createYoutubeIframe(node, HTMLAttributes, extension.options));

      const nodeView = new ResizableNodeView({
        element: el,
        editor,
        node,
        getPos,
        onResize: (width, height) => {
          // [미디어 리사이즈] 드래그 중 에디터 폭 초과 방지 — 작성/수정일: 2026-07-29
          syncResizableNodeMaxSize(nodeView, editor, minWidth);
          const clamped = clampEditorMediaSize(editor, width, height, minWidth);
          el.style.width = `${clamped.width}px`;
          el.style.height = `${clamped.height}px`;
          el.style.maxWidth = '100%';
        },
        onCommit: (width, height) => {
          const pos = getPos();
          if (pos === undefined) return;
          const clamped = clampEditorMediaSize(editor, width, height, minWidth);
          el.style.width = `${clamped.width}px`;
          el.style.maxWidth = '100%';
          editor
            .chain()
            .setNodeSelection(pos)
            .updateAttributes(this.name, { width: clamped.width, height: clamped.height })
            .run();
        },
        onUpdate: (updatedNode) => {
          if (updatedNode.type !== this.type) return false;
          const iframe = el.querySelector('iframe');
          if (iframe) {
            const src = buildEmbedSrc(updatedNode, extension.options);
            if (src && iframe.getAttribute('src') !== src) iframe.setAttribute('src', src);
          }
          syncYoutubeAlignment(el, updatedNode);
          syncYoutubeSize(el, updatedNode);
          applyAlignmentToContainer(nodeView.dom, resolveAlignment(updatedNode.attrs.alignment));
          return true;
        },
        options: {
          directions,
          min: { width: minWidth, height: minHeight },
          max: {
            width: syncResizableNodeMaxSize({ maxSize: {} }, editor, minWidth),
            height: 4000,
          },
          preserveAspectRatio: alwaysPreserveAspectRatio === true,
        },
      });

      applyAlignmentToContainer(nodeView.dom, resolveAlignment(node.attrs.alignment));
      // [미디어 리사이즈] 핸들 드래그 시작 시 현재 에디터 폭으로 max 갱신 — 작성/수정일: 2026-07-29
      nodeView.dom.addEventListener(
        'pointerdown',
        () => {
          syncResizableNodeMaxSize(nodeView, editor, minWidth);
        },
        true,
      );
      syncResizableNodeMaxSize(nodeView, editor, minWidth);
      return nodeView;
    };
  },
}).configure({
  nocookie: true,
  controls: true,
  modestBranding: true,
  width: 640,
  height: 360,
  HTMLAttributes: { class: 'editor-youtube', loading: 'lazy' },
});
